import type { TableView } from 'owa-mail-list-store';
import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getSenderNameForDisplay from 'owa-mail-store/lib/utils/getSenderNameForDisplay';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import type Message from 'owa-service/lib/contract/Message';

export function shouldShowRecipientPersona(tableView: TableView): boolean {
    if (!isGlobalFeatureEnabled('tri-mlCheckboxBehavior')) {
        return false;
    }

    const folderName =
        tableView.tableQuery?.folderId &&
        folderIdToName(tableView.tableQuery.folderId, true /* shouldSearchShared */);

    return folderName === 'drafts' || folderName === 'sentitems' || folderName === 'outbox';
}

/**
 * Matches the first name in rendered text ("Alice; Bob") to a known mailbox, else returns a
 * name-only mailbox so the coin shows that person's initials rather than someone else's photo.
 */
function resolvePersonaForDisplayedName(
    displayedNames: string | undefined,
    knownMailboxes: (EmailAddressWrapper | undefined)[],
    fallback: EmailAddressWrapper | undefined
): EmailAddressWrapper | undefined {
    const displayedName = displayedNames?.split(';')[0].trim();
    // Displayed text and candidate mailboxes come from different server fields, so normalize both
    // before comparing; this also collapses blanks that trim() misses, such as U+2800.
    const sanitizedName = getSenderNameForDisplay(displayedName).toLowerCase();

    if (!sanitizedName) {
        return fallback;
    }

    return (
        knownMailboxes.find(
            mailbox =>
                getSenderNameForDisplay(mailbox?.Name).toLowerCase() === sanitizedName ||
                getSenderNameForDisplay(mailbox?.EmailAddress).toLowerCase() === sanitizedName
        ) ?? { Name: displayedName }
    );
}

/** Resolves the sender or first recipient persona for a mail list row. */
export default function resolvePersonaMailboxForRow(
    rowKey: string,
    tableView: TableView,
    senderMailbox: EmailAddressWrapper | undefined
): EmailAddressWrapper | undefined {
    if (!shouldShowRecipientPersona(tableView)) {
        return senderMailbox;
    }

    const recipients =
        (MailRowDataPropertyGetter.getItem(rowKey, tableView) as Message | undefined)
            ?.ToRecipients ?? [];
    // Anchor on the name the row text shows first so the coin and the text always refer to the
    // same person. Item rows expose a single combined string ("Alice; Bob"), conversations a list.
    const recipientNames = MailRowDataPropertyGetter.getUniqueRecipients(rowKey, tableView);

    return resolvePersonaForDisplayedName(
        recipientNames?.find(name => !!name),
        recipients,
        recipients.find(recipient => !!recipient) ?? senderMailbox
    );
}

/**
 * Resolves the persona for an expanded item part, whose adjacent text is the sender name rather
 * than the row's recipients, so the coin matches the name rendered next to it.
 */
export function resolvePersonaMailboxForItemPart(
    displayedName: string | undefined,
    message: Message | undefined,
    tableView: TableView,
    senderMailbox: EmailAddressWrapper | undefined
): EmailAddressWrapper | undefined {
    if (!shouldShowRecipientPersona(tableView)) {
        return senderMailbox;
    }

    return resolvePersonaForDisplayedName(
        displayedName,
        [
            senderMailbox,
            message?.From?.Mailbox,
            ...(message?.ToRecipients ?? []),
            ...(message?.CcRecipients ?? []),
        ],
        senderMailbox
    );
}
